// Yandex Metrica — yalnızca app.penzapp.com; yalnızca anonim davranış eventleri.
import { useEffect, useRef } from "react";

export const COUNTER_ID = 114040869;
const PROD_HOST = "app.penzapp.com";
const REG_FLAG = "penz_ym_registration_pending";

export const GOALS = new Set([
  "registration_completed", "onboarding_started", "onboarding_completed",
  "customer_create_opened", "customer_created", "customer_create_failed",
  "job_create_opened", "job_created", "job_create_failed",
  "supplier_create_opened", "supplier_created", "supplier_create_failed",
  "purchase_create_opened", "purchase_created",
  "expense_create_opened", "expense_created", "expense_create_failed",
  "payment_create_opened", "payment_created",
  "report_opened", "subscription_page_opened",
]);
const USER_STATUSES = new Set(["trial", "paid"]);
const ERROR_TYPES = new Set(["validation", "network", "server", "unknown"]);
const FAILED_GOALS = new Set(["customer", "job", "supplier", "expense"]);

const state = { inited: false, lastUrl: null, status: null, policy: "unknown", needsReload: false, queue: [] };

// Yalnızca aktif, iç/test olmayan deneme (trial / süresi bitmiş trial) kullanıcıları izlenir.
export function isTrackableUser(user) {
  return !!user && user.account_status === "active" && user.subscription_status === "trial"
    && user.is_internal_account !== true;
}

// user: null = kontrol ediliyor, false = oturum yok, obje = oturumlu.
// Dönüş: "wait" | "track" | "blocked"
export function applyUserPolicy(user) {
  if (state.policy === "blocked") return "blocked";
  if (user === null) return "wait";
  if (user === false) return "track";
  if (isTrackableUser(user)) { state.policy = "allowed"; return "track"; }
  // Oturum öncesi sayaç çalıştıysa (paid/iç hesap girişi) Webvisor'ı durdurmak için tek yenileme.
  state.needsReload = state.inited && state.policy !== "allowed";
  state.policy = "blocked";
  state.queue = [];
  return "blocked";
}

export function consumeReload() {
  const r = state.needsReload;
  state.needsReload = false;
  return r;
}

const active = () => state.inited && state.policy !== "blocked";

export function isEnabled() {
  if (typeof window === "undefined") return false;
  return window.location.hostname === PROD_HOST || window.__PENZ_YM_FORCE__ === true;
}

function ym(...args) {
  try {
    if (typeof window.ym === "function") window.ym(COUNTER_ID, ...args);
  } catch (_) { /* analytics uygulamayı asla bozmamalı */ }
}

export function initMetrica() {
  if (state.inited || state.policy === "blocked" || !isEnabled()) return false;
  state.inited = true;
  /* eslint-disable */
  (function (m, e, t, r, i, k, a) {
    m[i] = m[i] || function () { (m[i].a = m[i].a || []).push(arguments); };
    m[i].l = 1 * new Date();
    for (let j = 0; j < e.scripts.length; j++) { if (e.scripts[j].src === r) return; }
    k = e.createElement(t); a = e.getElementsByTagName(t)[0];
    k.async = 1; k.src = r;
    if (a && a.parentNode) a.parentNode.insertBefore(k, a); else e.head.appendChild(k);
  })(window, document, "script", "https://mc.yandex.ru/metrika/tag.js", "ym");
  /* eslint-enable */
  // defer: ilk pageview'u route tracker gönderir → çift hit yok.
  ym("init", {
    defer: true, webvisor: true, clickmap: true, trackLinks: true,
    accurateTrackBounce: true, trackHash: false,
  });
  return true;
}

const ID_SEGMENT = /^(?:[0-9a-f]{16,}|[0-9a-f-]{32,36}|\d{3,}|[A-Za-z0-9_-]{24,})$/i;

// Sorgu dizesi (token, e-posta vb.) ve kayıt kimlikleri URL'den atılır; yalnızca utm_* kalır.
export function sanitizeUrl(pathname, search = "") {
  const path = pathname.split("/").map((s) => (ID_SEGMENT.test(s) ? ":id" : s)).join("/");
  const params = new URLSearchParams(search);
  const keep = new URLSearchParams();
  params.forEach((v, k) => { if (/^utm_(source|medium|campaign|content|term)$/.test(k)) keep.set(k, v); });
  const qs = keep.toString();
  return `${window.location.origin}${path}${qs ? `?${qs}` : ""}`;
}

export function trackPageview(pathname, search) {
  if (!active()) return false;
  const url = sanitizeUrl(pathname, search);
  if (url === state.lastUrl) return false;
  const referer = state.lastUrl || undefined;
  state.lastUrl = url;
  ym("hit", url, { title: "Penz", referer });
  state.queue.splice(0).forEach(([n, p]) => goal(n, p));
  return true;
}

export function goal(name, params) {
  if (!GOALS.has(name) || state.policy === "blocked") return;
  // Kullanıcı statüsü henüz bilinmiyorsa ilk pageview'a kadar beklet.
  if (!state.inited) { if (isEnabled() && state.queue.length < 20) state.queue.push([name, params]); return; }
  const safe = {};
  if (params && ERROR_TYPES.has(params.error_type)) safe.error_type = params.error_type;
  ym("reachGoal", name, Object.keys(safe).length ? safe : undefined);
}

export function setUserStatus(subscriptionStatus) {
  const s = USER_STATUSES.has(subscriptionStatus) ? subscriptionStatus : null;
  if (!active() || !s || s === state.status) return;
  state.status = s;
  ym("params", { user_status: s });
}

export function classifyError(err) {
  if (!err?.response) return err?.request || err?.code === "ERR_NETWORK" ? "network" : "unknown";
  const st = err.response.status;
  if (st === 400 || st === 409 || st === 422) return "validation";
  if (st >= 500) return "server";
  return "unknown";
}

// Kayıt: başarılı create → "<entity>_created"; hata → "<entity>_create_failed" + yeniden fırlatılır.
export async function trackCreate(entity, fn) {
  let result;
  try {
    result = await fn();
  } catch (err) {
    if (FAILED_GOALS.has(entity)) goal(`${entity}_create_failed`, { error_type: classifyError(err) });
    throw err;
  }
  goal(`${entity}_created`);
  return result;
}

export function markRegistrationCompleted() {
  try { window.sessionStorage.setItem(REG_FLAG, "1"); } catch (_) { /* yok say */ }
}

export function flushRegistrationCompleted() {
  if (!active()) return false;
  try {
    if (window.sessionStorage.getItem(REG_FLAG) !== "1") return false;
    window.sessionStorage.removeItem(REG_FLAG);
  } catch (_) { return false; }
  goal("registration_completed");
  return true;
}

// active false → true geçişinde bir kez goal gönderir.
export function useOpenedGoal(name, active) {
  const prev = useRef(false);
  useEffect(() => {
    if (active && !prev.current) goal(name);
    prev.current = !!active;
  }, [name, active]);
}

export function _resetForTests() {
  state.inited = false; state.lastUrl = null; state.status = null;
  state.policy = "unknown"; state.needsReload = false; state.queue = [];
}
